import { createContext, destroyContext, domToCanvas, loadMedia, type Context } from 'modern-screenshot';
import { skipSelector } from './config';
import { knownRanges } from './known';
import { BUG_OWN_ATTR, pauseBugRecorder } from './recorder';
import { SCREEN_BLUR_MEDIA, SCREEN_BLUR_TEXT, SCREEN_COVERED_ANY, SCREEN_MASK_CLASS, screenMaskOn } from './screenMask';
import { PIN_ATTR, applyPin, cutByPlan, planShot, type Region } from './shotPlan';
import { SENSITIVE_SELECTOR, piiSelector } from './snapshot';

// Window scroll is applied as a negative margin on the cloned body (not a transform), so position:fixed
// elements stay where they were. The clone has no scroll, so sticky elements get their measured offset.

const SHIFT_ATTR = 'data-bug-shift';
// Selected options and checkbox states are properties, not attributes: the clone would lose them, so they are marked.
const SELECTED_ATTR = 'data-bug-sel';
const CHECKED_ATTR = 'data-bug-chk';
const MASK_ATTR = 'data-bug-mask';
// What the screen mode covers on the page (Мс2): the shot hides it too.
const BLUR_ATTR = 'data-bug-blur';
const UNBLUR_ATTR = 'data-bug-unblur';
const MEDIA_TAG = /^(img|picture|video|canvas|svg|iframe|object|embed)$/i;
/**
 * What the panel holds of the person's words (fields, «по действиям», the agent chat): on a masked area shot that
 * takes the panel in (reports 103 / 106) it is covered like the names on the page: a field by the blur of the screen, a text holder by a plate.
 */
export const PANEL_TEXT_ATTR = 'data-bug-text';
/** The frame over a blank spot (report 103): one colour and nothing else — the shot fails with this, not a plain plate. */
export const EMPTY_AREA = 'в области ничего нет';
// The clone is re-rendered at the screen density (1.5–2, at most 2400 px wide); JPEG 0.85, over 1.5 MB once more at 0.72.
const MAX_WIDTH_PX = 2400;
const MAX_SCALE = 2;
const MIN_SCALE = 1.5;
const TARGET_BYTES = 1.5 * 1024 * 1024;
const QUALITIES = [0.85, 0.72];
const RESOURCE_TIMEOUT_MS = 4_000;
// Images of the shot that are still loading are waited for this long; images the shot leaves out are not waited for.
const IMAGE_WAIT_MS = 1_500;
// A dialog or menu that is still fading in is shot once it is shown (its opacity mid-way would be cloned).
const ANIMATION_WAIT_MS = 600;
// Image data kept between shots (the second shot of the same page fetches nothing).
const IMAGE_CACHE_ENTRIES = 300;
const IMAGE_CACHE_CHARS = 12 * 1024 * 1024;
// An image the page shows but the shot cannot fetch (another origin without CORS): a neutral grey box.
const IMAGE_PLACEHOLDER =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8"><rect width="8" height="8" fill="#9a958d" fill-opacity="0.35"/></svg>');

export function shotScale(width: number, dpr: number): number {
  return Math.max(1, Math.min(Math.max(dpr || 1, MIN_SCALE), MAX_SCALE, MAX_WIDTH_PX / Math.max(1, width)));
}

export async function encodeJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  let last: Blob | null = null;
  for (const quality of QUALITIES) {
    last = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!last) break;
    if (last.size <= TARGET_BYTES) return last;
  }
  if (!last) throw new Error('браузер не отдал картинку');
  return last;
}

function markFormState(): Element[] {
  const marked: Element[] = [];
  for (const select of document.body.querySelectorAll('select')) {
    const picked = [...select.options].flatMap((option, index) => (option.selected ? [index] : []));
    select.setAttribute(SELECTED_ATTR, picked.join(','));
    marked.push(select);
  }
  for (const input of document.body.querySelectorAll<HTMLInputElement>('input[type=checkbox], input[type=radio]')) {
    input.setAttribute(CHECKED_ATTR, input.checked ? '1' : '0');
    marked.push(input);
  }
  return marked;
}

function mirrorFormState(cloned: HTMLElement): void {
  const picked = cloned.getAttribute(SELECTED_ATTR);
  if (picked !== null && cloned instanceof HTMLSelectElement) {
    cloned.removeAttribute(SELECTED_ATTR);
    const on = new Set(picked.split(',').filter(Boolean).map(Number));
    [...cloned.options].forEach((option, index) => {
      option.toggleAttribute('selected', on.has(index));
      option.selected = on.has(index);
    });
    return;
  }
  const checked = cloned.getAttribute(CHECKED_ATTR);
  if (checked !== null && cloned instanceof HTMLInputElement) {
    cloned.removeAttribute(CHECKED_ATTR);
    cloned.toggleAttribute('checked', checked === '1');
    cloned.checked = checked === '1';
    return;
  }
  // The clone keeps a field value as the `value` attribute, but a textarea shows its content.
  if (cloned instanceof HTMLTextAreaElement) cloned.textContent = cloned.getAttribute('value') ?? cloned.value;
}

/**
 * The shot's own choice of the screen cover (Ок6): `on` covers what the screen mode covers, even where the page itself is
 * open; off with the page covered strips the blur the clone would copy from the page. Returns the nodes to unmark.
 */
function markScreenCovered(on: boolean): Element[] {
  if (on) {
    const marked = [...document.body.querySelectorAll(SCREEN_COVERED_ANY)];
    for (const el of marked) el.setAttribute(BLUR_ATTR, '');
    return marked;
  }
  if (!document.documentElement.classList.contains(SCREEN_MASK_CLASS)) return [];
  const all = [...document.body.querySelectorAll('*')];
  for (const el of all) el.setAttribute(UNBLUR_ATTR, '');
  return all;
}

/** The blur of the screen on the text of one clone (Мс4, Мс6): glyphs transparent, the shadow of the text blurred; no plate, no «█». */
function blurText(el: HTMLElement | SVGElement): void {
  el.style.setProperty('-webkit-text-fill-color', 'transparent', 'important');
  el.style.setProperty('caret-color', 'transparent', 'important');
  el.style.setProperty('text-shadow', SCREEN_BLUR_TEXT, 'important');
}

/**
 * The clone gets the blur of the screen as its own inline style instead of trusting the copied computed one. Media do
 * not keep the mark when the library turns them into pictures (a canvas, a video), but they keep the copied filter of
 * the screen sheet: a picture becomes the neutral box, other media are left out of sight — a CSS filter is not drawn
 * the same by every browser in the shot's SVG.
 */
export function blurClone(cloned: Element): void {
  const style = (cloned as HTMLElement | SVGElement).style;
  if (!style) return;
  if (cloned.hasAttribute(UNBLUR_ATTR)) {
    cloned.removeAttribute(UNBLUR_ATTR);
    if (/^(transparent|rgba\(0, 0, 0, 0\))$/.test(style.getPropertyValue('-webkit-text-fill-color'))) {
      style.setProperty('-webkit-text-fill-color', 'currentColor', 'important');
      style.setProperty('caret-color', 'auto', 'important');
      style.setProperty('text-shadow', 'none', 'important');
    }
    if (style.getPropertyValue('filter') === SCREEN_BLUR_MEDIA) style.setProperty('filter', 'none', 'important');
    return;
  }
  const covered = cloned.hasAttribute(BLUR_ATTR);
  if (covered) {
    cloned.removeAttribute(BLUR_ATTR);
    blurText(cloned as HTMLElement | SVGElement);
  }
  if (!MEDIA_TAG.test(cloned.localName) || !(covered || style.getPropertyValue('filter') === SCREEN_BLUR_MEDIA)) return;
  style.setProperty('filter', 'none', 'important');
  if (cloned instanceof HTMLImageElement) {
    cloned.removeAttribute('srcset');
    cloned.src = IMAGE_PLACEHOLDER;
  } else style.setProperty('visibility', 'hidden', 'important');
}

/**
 * Мс6: what the host marked (`piiSelector()`) is blurred on the shot the way the screen blurs it — the element and
 * everything inside it, a field keeps its value under the blur — so the screen and the shot show one kind. The report
 * panel's own text (`PANEL_TEXT_ATTR`) stays a plate or blocks: it is the widget's text, not the host's mark.
 */
function markPii(): Element[] {
  const hostMarked = piiSelector() ? [...document.body.querySelectorAll(piiSelector())] : [];
  const covered = new Set<Element>();
  for (const el of hostMarked) {
    covered.add(el);
    for (const inner of el.querySelectorAll('*')) covered.add(inner);
  }
  for (const el of covered) el.setAttribute(BLUR_ATTR, '');
  const panel = [...document.body.querySelectorAll(`[${PANEL_TEXT_ATTR}]`)].filter((el) => !covered.has(el));
  for (const el of panel) el.setAttribute(MASK_ATTR, '');
  return [...covered, ...panel];
}

/**
 * A plate instead of text: the text stays in place and keeps its width but is invisible, and its box is painted with the
 * text color. The layout of the clone does not move (a "█" line is wider than letters and wrapped lines out of bubbles),
 * and the plate follows the theme by itself.
 */
export function plate(el: HTMLElement): void {
  el.style.setProperty('-webkit-text-fill-color', 'transparent', 'important');
  el.style.setProperty('background-color', 'currentColor', 'important');
  el.style.setProperty('background-image', 'none', 'important');
  el.style.setProperty('text-shadow', 'none', 'important');
  el.style.setProperty('border-radius', '2px', 'important');
}

function plateKnown(node: Text): void {
  const value = node.nodeValue ?? '';
  const ranges = knownRanges(value);
  if (ranges.length === 0 || !node.parentNode) return;
  const doc = node.ownerDocument;
  const out = doc.createDocumentFragment();
  let pos = 0;
  for (const range of ranges) {
    if (range.start > pos) out.appendChild(doc.createTextNode(value.slice(pos, range.start)));
    const span = doc.createElement('span');
    span.textContent = value.slice(range.start, range.end);
    plate(span);
    out.appendChild(span);
    pos = range.end;
  }
  if (pos < value.length) out.appendChild(doc.createTextNode(value.slice(pos)));
  node.parentNode.replaceChild(out, node);
}

export function maskClone(cloned: Element): void {
  if (cloned.hasAttribute(MASK_ATTR)) {
    cloned.removeAttribute(MASK_ATTR);
    // Мс6: a field keeps its box, lines and value, the value is blurred as on the screen (a plate would paint the whole
    // field, «█» is not the kind the screen shows).
    if (cloned instanceof HTMLTextAreaElement || cloned instanceof HTMLInputElement) {
      blurText(cloned);
      return;
    }
    if (cloned instanceof HTMLElement) plate(cloned);
    // Children were cloned with their computed styles: an explicit fill color on them would beat inheritance.
    for (const inner of cloned.querySelectorAll<HTMLElement>('*')) inner.style?.setProperty('-webkit-text-fill-color', 'transparent', 'important');
    return;
  }
  // Text nodes of the clone never reach onCloneEachNode: take the direct children of every element.
  for (const node of [...cloned.childNodes]) {
    if (node.nodeType === Node.TEXT_NODE && node.nodeValue && /\S/.test(node.nodeValue)) plateKnown(node as Text);
  }
  // Мс6: a field with a known name in its value is blurred whole, the way the screen does (`scanField`).
  if (cloned instanceof HTMLInputElement && knownRanges(cloned.getAttribute('value') ?? '').length > 0) blurText(cloned);
  if (cloned instanceof HTMLTextAreaElement && knownRanges(cloned.textContent ?? '').length > 0) blurText(cloned);
}

function markStickyShifts(cut: (el: Element) => boolean): Element[] {
  const marked: Element[] = [];
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => (cut(node as Element) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const el = node as Element;
    if (!(el instanceof HTMLElement) || getComputedStyle(el).position !== 'sticky') continue;
    const stuck = el.getBoundingClientRect();
    const inline = el.style.position;
    el.style.position = 'static';
    const natural = el.getBoundingClientRect();
    el.style.position = inline;
    const dx = Math.round(stuck.left - natural.left);
    const dy = Math.round(stuck.top - natural.top);
    if (dx || dy) {
      el.setAttribute(SHIFT_ATTR, `${dx},${dy}`);
      marked.push(el);
    }
  }
  return marked;
}

/**
 * The scheme the page is drawn in: its own color-scheme, the system one only when the page allows both. A page without
 * color-scheme is drawn light whatever the system says.
 */
export function usedColorScheme(): 'light' | 'dark' {
  const scheme = getComputedStyle(document.documentElement).colorScheme ?? '';
  const dark = /\bdark\b/.test(scheme);
  const light = /\blight\b/.test(scheme);
  if (dark && light) return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  return dark ? 'dark' : 'light';
}

/**
 * The library leaves out of each cloned element the values equal to a fresh element's defaults, read in a blank frame.
 * The frame takes the page scheme: in a dark page a light default (white field, black button text) is a real value
 * and must stay, and the dark defaults are the ones the clone gets anyway.
 */
function defaultsFrame(scheme: 'light' | 'dark'): HTMLIFrameElement {
  const frame = document.createElement('iframe');
  frame.setAttribute(BUG_OWN_ATTR, '');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden;pointer-events:none';
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  if (doc) {
    doc.open();
    doc.write(`<!DOCTYPE html><html style="color-scheme:${scheme}"><head><meta charset="UTF-8"><title></title></head><body></body></html>`);
    doc.close();
  }
  return frame;
}

let styleNames: string[] | null = null;

/** Standard properties only: custom properties are already resolved in them, and a theme defines hundreds. */
function standardProperties(): string[] {
  if (!styleNames) styleNames = [...getComputedStyle(document.documentElement)].filter((name) => !name.startsWith('--'));
  return styleNames;
}

const imageCache = new Map<string, string>();
let imageCacheChars = 0;

function rememberImage(url: string, data: string): void {
  if (data.length > IMAGE_CACHE_CHARS / 4) return;
  imageCache.set(url, data);
  imageCacheChars += data.length;
  for (const [key, value] of imageCache) {
    if (imageCache.size <= IMAGE_CACHE_ENTRIES && imageCacheChars <= IMAGE_CACHE_CHARS) break;
    imageCache.delete(key);
    imageCacheChars -= value.length;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Image data for the clone: cached between shots; an image that cannot be fetched becomes the placeholder at once. */
export async function shotImage(url: string): Promise<string> {
  const cached = imageCache.get(url);
  if (cached) return cached;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), RESOURCE_TIMEOUT_MS);
  try {
    const response = await fetch(url, { cache: 'force-cache', signal: controller.signal });
    if (!response.ok) throw new Error(String(response.status));
    const data = await blobToDataUrl(await response.blob());
    rememberImage(url, data);
    return data;
  } catch {
    rememberImage(url, IMAGE_PLACEHOLDER);
    return IMAGE_PLACEHOLDER;
  } finally {
    clearTimeout(timer);
  }
}

/** Forgets cached image data (tests). */
export function clearShotCache(): void {
  imageCache.clear();
  imageCacheChars = 0;
}

export function bodyBackground(): string {
  const body = getComputedStyle(document.body).backgroundColor;
  if (body && body !== 'rgba(0, 0, 0, 0)' && body !== 'transparent') return body;
  // A host without any page background: the canvas colour the browser shows in the page scheme (transparent would come
  // out black in a JPEG; white would be a light patch on a dark page).
  const root = getComputedStyle(document.documentElement).backgroundColor;
  if (root && root !== 'rgba(0, 0, 0, 0)' && root !== 'transparent') return root;
  return usedColorScheme() === 'dark' ? '#121212' : '#ffffff';
}

export interface CaptureOptions {
  /** Щт1: false — the person turned the shield off (the host gave the switch): nothing of the shot is covered. Default true (Мс3). */
  hideNames?: boolean;
  /** Ок6: cover what the screen mode covers (the shield of the tools row); default — the screen mode itself. */
  coverScreen?: boolean;
  cropRight?: number;
  area?: { x: number; y: number; w: number; h: number };
}

function cropCanvas(canvas: HTMLCanvasElement, cropRight: number, width: number, scale: number): HTMLCanvasElement {
  const keep = Math.round((width - cropRight) * scale);
  if (cropRight <= 0 || keep <= 0 || keep >= canvas.width) return canvas;
  const out = document.createElement('canvas');
  out.width = keep;
  out.height = canvas.height;
  out.getContext('2d')?.drawImage(canvas, 0, 0);
  return out;
}

export function cropArea(canvas: HTMLCanvasElement, area: { x: number; y: number; w: number; h: number }, scale: number): HTMLCanvasElement {
  const x = Math.max(0, Math.round(area.x * scale));
  const y = Math.max(0, Math.round(area.y * scale));
  const w = Math.min(canvas.width - x, Math.round(area.w * scale));
  const h = Math.min(canvas.height - y, Math.round(area.h * scale));
  if (w <= 0 || h <= 0) return canvas;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  out.getContext('2d')?.drawImage(canvas, x, y, w, h, 0, 0, w, h);
  return out;
}

/** Every pixel the same colour: the frame caught nothing (report 103). Without pixel access (tests) — not uniform. */
export function uniformCanvas(canvas: HTMLCanvasElement): boolean {
  if (canvas.width === 0 || canvas.height === 0) return false;
  let data: Uint8ClampedArray;
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  } catch {
    return false;
  }
  const [r, g, b, a] = data;
  for (let i = 4; i < data.length; i += 4) {
    if (data[i] !== r || data[i + 1] !== g || data[i + 2] !== b || data[i + 3] !== a) return false;
  }
  return true;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Finite page animations (not the widget's) that are still running, waited for up to `ANIMATION_WAIT_MS`. */
export async function settleAnimations(maxMs = ANIMATION_WAIT_MS): Promise<void> {
  if (typeof document.getAnimations !== 'function') return;
  const running = document.getAnimations().filter((animation) => {
    if (animation.playState !== 'running') return false;
    const target = animation.effect instanceof KeyframeEffect ? animation.effect.target : null;
    if (target && target.closest(`[${BUG_OWN_ATTR}]`)) return false;
    const end = Number(animation.effect?.getComputedTiming().endTime);
    return Number.isFinite(end);
  });
  if (running.length === 0) return;
  await Promise.race([Promise.all(running.map((animation) => animation.finished.catch(() => undefined))), delay(maxMs)]);
}

/** A shot with what the log of the tool keeps about it (reports 105–107): its size in pixels and how long it took. */
export interface TakenShot {
  blob: Blob;
  px: { w: number; h: number };
  ms: number;
}

export async function captureViewport(skip: (el: Element) => boolean, options: CaptureOptions = {}): Promise<TakenShot> {
  const started = performance.now();
  const width = window.innerWidth;
  const height = window.innerHeight;
  const scale = shotScale(width, window.devicePixelRatio);
  const hostSkip = skipSelector();
  const skipped = (el: Element) => skip(el) || (!!hostSkip && el.matches(hostSkip));
  const region: Region = options.area
    ? { left: options.area.x, top: options.area.y, right: options.area.x + options.area.w, bottom: options.area.y + options.area.h }
    : { left: 0, top: 0, right: width - Math.max(0, options.cropRight ?? 0), bottom: height };
  await settleAnimations();
  const plan = planShot(document.body, region, skipped);
  const scheme = usedColorScheme();
  const hide = options.hideNames !== false;
  const marked = [...plan.pinned, ...markStickyShifts((el) => plan.skip.has(el) || plan.stubs.has(el) || skipped(el)), ...markFormState(), ...(hide ? [...markScreenCovered(options.coverScreen ?? screenMaskOn()), ...markPii()] : [])];
  let context: Context | null = null;
  pauseBugRecorder(true);
  try {
    // Only the images the shot keeps are waited for (the library would wait for every image of the page, lazy ones
    // below the fold included, up to its timeout).
    await Promise.all(plan.images.map((img) => loadMedia(img, { timeout: IMAGE_WAIT_MS })));
    context = await createContext(document.createElement('div'), {
      width,
      height,
      scale,
      backgroundColor: bodyBackground(),
      font: false,
      timeout: RESOURCE_TIMEOUT_MS,
      fetchFn: shotImage,
      includeStyleProperties: standardProperties(),
      features: { restoreScrollPosition: true },
      style: { margin: `${-window.scrollY}px 0 0 ${-window.scrollX}px`, colorScheme: scheme },
      filter: (node) => !cutByPlan(node, plan) && !(node instanceof Element && skipped(node)),
      onCloneEachNode: (cloned) => {
        if (cloned instanceof Element) blurClone(cloned);
        if (!(cloned instanceof HTMLElement)) return;
        applyPin(cloned);
        mirrorFormState(cloned);
        // Marked names are hidden on every shot, with the screen mode or without it (Мс3) — unless the shield was turned off (Щт1).
        if (hide) maskClone(cloned);
        const shift = cloned.getAttribute(SHIFT_ATTR);
        if (!shift) return;
        const [dx, dy] = shift.split(',').map(Number);
        cloned.removeAttribute(SHIFT_ATTR);
        cloned.style.position = 'relative';
        cloned.style.top = `${dy}px`;
        cloned.style.left = `${dx}px`;
      },
      onCreateForeignObjectSvg: () => {
        // Safari and Firefox redraw the picture once per embedded image, 100 ms apart; a few redraws are enough.
        if (context) context.drawImageCount = Math.min(context.drawImageCount, 2);
      },
    });
    // The context was made on an empty node (nothing to wait for); the shot is of the page.
    context.node = document.body;
    context.sandbox = defaultsFrame(scheme);
    const canvas = await domToCanvas(context);
    const cut = options.area ? cropArea(canvas, options.area, scale) : cropCanvas(canvas, options.cropRight ?? 0, width, scale);
    if (options.area && uniformCanvas(cut)) throw new Error(EMPTY_AREA);
    const blob = await encodeJpeg(cut);
    return { blob, px: { w: cut.width, h: cut.height }, ms: Math.round(performance.now() - started) };
  } finally {
    if (context) destroyContext(context);
    pauseBugRecorder(false);
    for (const el of marked) {
      el.removeAttribute(PIN_ATTR);
      el.removeAttribute(SHIFT_ATTR);
      el.removeAttribute(SELECTED_ATTR);
      el.removeAttribute(CHECKED_ATTR);
      el.removeAttribute(MASK_ATTR);
      el.removeAttribute(BLUR_ATTR);
      el.removeAttribute(UNBLUR_ATTR);
    }
  }
}

/** Two frames: a closed menu (or a hidden report panel) leaves the page before the shot. */
export function nextPaint(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

export function skipOnShot(el: Element): boolean {
  return el.hasAttribute(BUG_OWN_ATTR) || el.classList.contains('bug-report-overlay') || el.matches(SENSITIVE_SELECTOR);
}

/**
 * A frame the person drew takes what it covers, the report panel and its thumbnails included (reports 103 / 106: the
 * frame over the panel gave an empty plate); only the frame overlay itself, the shot viewer and the defaults frame of
 * the shot are left out.
 */
export function skipOnAreaShot(el: Element): boolean {
  if (el.hasAttribute(BUG_OWN_ATTR)) return el.classList.contains('bug-area') || el.classList.contains('bug-viewer') || el.localName === 'iframe';
  return el.classList.contains('bug-report-overlay') || el.matches(SENSITIVE_SELECTOR);
}

/** Rects are fractions of the image (0–1). */
export async function composeShot(image: Blob, rects: ShotRect[]): Promise<Blob> {
  if (rects.length === 0) return image;
  const bitmap = await createImageBitmap(image);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return image;
  ctx.drawImage(bitmap, 0, 0);
  ctx.strokeStyle = '#e5302a';
  ctx.lineWidth = Math.max(3, Math.round(bitmap.width / 400));
  for (const r of rects) {
    ctx.strokeRect(r.x * bitmap.width, r.y * bitmap.height, r.w * bitmap.width, r.h * bitmap.height);
  }
  bitmap.close();
  return encodeJpeg(canvas).catch(() => image);
}

/** A shot for the model: frames drawn, the long side at most `maxSide`, as a JPEG data URL (the same masked picture the report carries). */
export async function shotForModel(image: Blob, rects: ShotRect[], maxSide = 1280): Promise<string> {
  const bitmap = await createImageBitmap(image);
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('браузер не дал нарисовать снимок');
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = '#e5302a';
  ctx.lineWidth = Math.max(2, Math.round(canvas.width / 400));
  for (const r of rects) ctx.strokeRect(r.x * canvas.width, r.y * canvas.height, r.w * canvas.width, r.h * canvas.height);
  bitmap.close();
  return canvas.toDataURL('image/jpeg', 0.8);
}

export interface ShotRect {
  x: number;
  y: number;
  w: number;
  h: number;
}
